import { useState, useRef, useEffect } from 'react';
import { Button } from '@safetyhub/ui';

interface ActionItemsInputProps {
  items: string[];
  onChange: (items: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * 조치사항 항목 단위 입력 컴포넌트
 * 항목 추가/수정/삭제를 지원하며, \n으로 구분된 notes 문자열과 호환
 */
export function ActionItemsInput({
  items,
  onChange,
  disabled = false,
  placeholder = '새 항목 입력...',
}: ActionItemsInputProps) {
  const [newItem, setNewItem] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);
  const newInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingIndex !== null && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingIndex]);

  const handleAdd = () => {
    const trimmed = newItem.trim();
    if (!trimmed) return;
    onChange([...items, trimmed]);
    setNewItem('');
    newInputRef.current?.focus();
  };

  const handleRemove = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
    }
  };

  const handleEditStart = (index: number) => {
    setEditingIndex(index);
    setEditingText(items[index]);
  };

  const handleEditConfirm = () => {
    if (editingIndex === null) return;
    const trimmed = editingText.trim();
    if (trimmed) {
      const updated = [...items];
      updated[editingIndex] = trimmed;
      onChange(updated);
    }
    setEditingIndex(null);
  };

  const handleEditCancel = () => {
    setEditingIndex(null);
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditConfirm();
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  const handleNewKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="space-y-2">
      {/* 항목 목록 */}
      {items.length > 0 && (
        <ul className="space-y-1">
          {items.map((item, index) => (
            <li
              key={index}
              className="flex items-center gap-2 group"
            >
              <span className="text-xs text-ink-400 w-5 text-right flex-shrink-0">
                {index + 1}.
              </span>

              {editingIndex === index ? (
                <input
                  ref={editInputRef}
                  type="text"
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  onBlur={handleEditConfirm}
                  onKeyDown={handleEditKeyDown}
                  disabled={disabled}
                  className="flex-1 px-2 py-1 text-sm border border-brand-400 rounded-control focus:ring-1 focus:ring-brand-500 focus:outline-none"
                />
              ) : (
                <>
                  <span
                    className="flex-1 text-sm text-ink-800 leading-5 cursor-pointer rounded px-1 -mx-1 hover:bg-surface-hover transition-colors"
                    onClick={() => !disabled && handleEditStart(index)}
                    title="클릭하여 수정"
                  >
                    {item}
                  </span>
                  {!disabled && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleEditStart(index)}
                        className="p-0.5 text-ink-400 hover:text-brand-600"
                        title="수정"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemove(index)}
                        className="p-0.5 text-ink-400 hover:text-red-600"
                        title="삭제"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* 새 항목 추가 */}
      {!disabled && (
        <div className="flex gap-2">
          <input
            ref={newInputRef}
            type="text"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={handleNewKeyDown}
            placeholder={placeholder}
            className="flex-1 px-3 py-1.5 text-sm border border-ink-300 rounded-control focus:ring-brand-500 focus:border-brand-500"
          />
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="flex-shrink-0"
            onClick={handleAdd}
            disabled={!newItem.trim()}
          >
            추가
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * notes 문자열을 항목 배열로 변환
 */
export function notesToItems(notes: string): string[] {
  return notes.split('\n').filter(Boolean);
}

/**
 * 항목 배열을 notes 문자열로 변환
 */
export function itemsToNotes(items: string[]): string {
  return items.join('\n');
}

/** 원문자(①②③…) 접두사 제거 */
function stripCircledNumber(text: string): string {
  return text.replace(/^[\u2460-\u2473\u3251-\u325F\u32B1-\u32BF]\s*/, '');
}

/**
 * notes를 렌더링 (여러 줄이면 목록, 단일이면 텍스트)
 */
export function renderNotes(notes: string) {
  const items = notes.split('\n').filter(Boolean).map(stripCircledNumber);
  if (items.length <= 1) return <span>{items[0] || notes}</span>;
  return (
    <ul className="list-none space-y-0.5">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

/**
 * print 모드에서 notes 렌더링 (시맨틱 CSS용)
 */
export function renderNotesPrint(notes: string) {
  const items = notes.split('\n').filter(Boolean).map(stripCircledNumber);
  if (items.length <= 1) return <>{items[0] || notes}</>;
  return (
    <div style={{ margin: 0 }}>
      {items.map((item, i) => (
        <div key={i}>{item}</div>
      ))}
    </div>
  );
}

export default ActionItemsInput;
